import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../api';
import { Autocomplete, TextField } from '@mui/material';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: number;
  lastLoginAt: number;
  lastAccessedAt: number;
}

interface AdminProps {
  onOpenSettings?: () => void;
}

const Admin: React.FC<AdminProps> = ({ onOpenSettings }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
  });
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [showModelModal, setShowModelModal] = useState(false);
  const [models, setModels] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [showAllModels, setShowAllModels] = useState(true);
  const [timeoutValues, setTimeoutValues] = useState<{[key: string]: number}>({});
  const [maxOutputTokensValues, setMaxOutputTokensValues] = useState<{[key: string]: number}>({});
  const [rescanLoading, setRescanLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [showDeprecatedModels, setShowDeprecatedModels] = useState(false);
  const [modelSearchText, setModelSearchText] = useState('');

  // API Key Management State
  const [showAPIKeyModal, setShowAPIKeyModal] = useState(false);
  const [apiKeys, setAPIKeys] = useState<any[]>([]);
  const [apiKeysLoading, setAPIKeysLoading] = useState(false);
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [newAPIKey, setNewAPIKey] = useState({
    provider: '',
    name: '',
    keyValue: ''
  });

  // Prompt Optimization Settings State
  const [promptOptimizerSettings, setPromptOptimizerSettings] = useState({
    model: 'gpt-4o-mini',
    prompt: 'You are a Prompt Enhancer. Your job is to take any user-submitted prompt and transform it into a clearer, more powerful, and context-aware version that delivers better results when used with large language models.'
  });
  const [promptOptimizerLoading, setPromptOptimizerLoading] = useState(false);

  // Timeout Monitoring State
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [timeoutMetrics, setTimeoutMetrics] = useState<any>(null);

  // Cache Management State
  const [showCacheWipeModal, setShowCacheWipeModal] = useState(false);
  const [cacheWipeConfirmation, setCacheWipeConfirmation] = useState('');
  const [timeoutLoading, setTimeoutLoading] = useState(false);

  // Version State
  const [versionInfo, setVersionInfo] = useState<any>(null);
  
  // Ref for models list container to preserve scroll position
  const modelsContainerRef = useRef<HTMLDivElement>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user || user.role !== 'admin') {
      navigate('/');
      toast.error('You do not have permission to access the admin page');
      return;
    }
    
    fetchUsers();
    fetchRegistrationSettings();
    fetchVersionInfo();
    fetchPromptOptimizerSettings();
    fetchModels(); // Fetch models for prompt optimizer settings dropdown
  }, [navigate]);

  const fetchRegistrationSettings = async () => {
    try {
      const settings = await api.admin.getRegistrationSettings();
      setRegistrationEnabled(settings.enabled);
    } catch (err) {
      console.error('Error fetching registration settings:', err);
      // Default to enabled if we can't fetch
      setRegistrationEnabled(true);
    }
  };

  const handleRegistrationToggle = async (enabled: boolean) => {
    setSettingsLoading(true);
    try {
      await api.admin.updateRegistrationSettings(enabled);
      setRegistrationEnabled(enabled);
      toast.success(`Registration has been ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error updating registration settings:', err);
      toast.error('Failed to update registration settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchPromptOptimizerSettings = async () => {
    try {
      const settings = await api.getPromptOptimizerSettings();
      setPromptOptimizerSettings(settings);
    } catch (err) {
      console.error('Error fetching prompt optimizer settings:', err);
      // Use defaults
    }
  };

  const handleSavePromptOptimizerSettings = async () => {
    setPromptOptimizerLoading(true);
    try {
      await api.updatePromptOptimizerSettings(promptOptimizerSettings);
      toast.success('Prompt optimizer settings saved successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save prompt optimizer settings');
    } finally {
      setPromptOptimizerLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      console.log('Fetching users with token:', token.substring(0, 10) + '...');
      
      const data = await api.admin.getUsers();
      console.log(`Fetched ${data.length} users`);
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setUsers([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;


    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const updatedUser = await api.admin.updateUser(editingUser.id, formData);
      
      // Update user in the list
      setUsers(prev => prev.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      ));
      
      setEditingUser(null);
      
      toast.success('User updated successfully');
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteConfirmUser(user);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmUser) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      await api.admin.deleteUser(deleteConfirmUser.id);

      // Remove user from the list
      setUsers(prev => prev.filter(user => user.id !== deleteConfirmUser.id));
      setDeleteConfirmUser(null);
      toast.success('User deleted successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmUser(null);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleCacheWipe = async () => {
    if (cacheWipeConfirmation !== 'WIPE CACHE') {
      toast.error('Please type "WIPE CACHE" to confirm');
      return;
    }

    try {
      const result = await api.admin.wipeCache(cacheWipeConfirmation);
      toast.success(result.message || 'Cache wiped successfully');
      setShowCacheWipeModal(false);
      setCacheWipeConfirmation('');
    } catch (err) {
      console.error('Error wiping cache:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to wipe cache');
    }
  };

  const handleExportBackup = async () => {
    setBackupLoading(true);
    try {
      const backupData = await api.admin.exportBackup();
      
      // Create a downloadable file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Backup exported successfully');
    } catch (err) {
      console.error('Error exporting backup:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to export backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setImportFile(file || null);
  };

  const handleImportBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importFile) return;

    setBackupLoading(true);
    try {
      const fileContent = await importFile.text();
      const backupData = JSON.parse(fileContent);
      
      const result = await api.admin.importBackup(backupData);
      
      if (result.success) {
        toast.success(`Backup imported successfully: ${result.usersImported} users, ${result.workflowsImported} workflows, ${result.blocksImported} blocks, ${result.agentsImported} agents`);
        setShowImportModal(false);
        setImportFile(null);
        fetchUsers(); // Refresh the user list
      } else {
        toast.error(`Import completed with errors: ${result.message}`);
        if (result.errors && result.errors.length > 0) {
          console.error('Import errors:', result.errors);
        }
      }
    } catch (err) {
      console.error('Error importing backup:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to import backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const fetchModels = async () => {
    setModelsLoading(true);
    try {
      const response = await api.admin.getModels(showAllModels);
      setModels(response.models || []);
    } catch (err) {
      console.error('Error fetching models:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to fetch models');
    } finally {
      setModelsLoading(false);
    }
  };

  // Fetch models while preserving scroll position
  const fetchModelsPreservingScroll = async () => {
    // Save current scroll position
    const scrollTop = modelsContainerRef.current?.scrollTop || 0;
    
    // Fetch models
    await fetchModels();
    
    // Restore scroll position after React re-renders
    setTimeout(() => {
      if (modelsContainerRef.current) {
        modelsContainerRef.current.scrollTop = scrollTop;
      }
    }, 0);
  };

  const fetchProviders = async () => {
    try {
      const response = await api.admin.getProviders();
      setProviders(response.providers || []);
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  };

  const handleModelStatusToggle = async (modelId: string, enabled: boolean) => {
    try {
      await api.admin.updateModelStatus(modelId, enabled);
      toast.success(`Model ${enabled ? 'enabled' : 'disabled'} successfully`);
      fetchModelsPreservingScroll(); // Refresh the list while preserving scroll
    } catch (err) {
      console.error('Error updating model status:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update model status');
    }
  };

  const handleSetDefaultModel = async (modelId: string) => {
    try {
      await api.admin.setDefaultModel(modelId);
      toast.success('Default model set successfully');
      fetchModels(); // Refresh the list
    } catch (err) {
      console.error('Error setting default model:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to set default model');
    }
  };

  const handleCacheableToggle = async (modelId: string, cacheable: boolean) => {
    try {
      await api.admin.updateModelCacheable(modelId, cacheable);
      toast.success(`Model caching ${cacheable ? 'enabled' : 'disabled'} successfully`);
      fetchModelsPreservingScroll(); // Refresh the list while preserving scroll
    } catch (err) {
      console.error('Error updating model cacheable status:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update cacheable status');
    }
  };

  const handleTimeoutChange = (modelId: string, timeout: number) => {
    setTimeoutValues(prev => ({
      ...prev,
      [modelId]: timeout
    }));
  };

  const handleTimeoutUpdate = async (modelId: string, timeout: number) => {
    if (isNaN(timeout) || timeout < 5 || timeout > 600) {
      toast.error('Timeout must be between 5 and 600 seconds');
      return;
    }

    try {
      await api.admin.updateModelTimeout(modelId, timeout);
      toast.success('Model timeout updated successfully');
      fetchModelsPreservingScroll(); // Refresh the list while preserving scroll
    } catch (err) {
      console.error('Error updating model timeout:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update model timeout');
      // Reset the input value on error
      setTimeoutValues(prev => {
        const newValues = { ...prev };
        delete newValues[modelId];
        return newValues;
      });
    }
  };

  const handleMaxOutputTokensChange = (modelId: string, maxOutputTokens: number) => {
    setMaxOutputTokensValues(prev => ({
      ...prev,
      [modelId]: maxOutputTokens
    }));
  };

  const handleMaxOutputTokensUpdate = async (modelId: string, maxOutputTokens: number) => {
    if (isNaN(maxOutputTokens) || maxOutputTokens < 100 || maxOutputTokens > 128000) {
      toast.error('Max output tokens must be between 100 and 128000');
      return;
    }

    try {
      await api.admin.updateModelMaxOutputTokens(modelId, maxOutputTokens);
      toast.success('Model max output tokens updated successfully');
      fetchModelsPreservingScroll(); // Refresh the list while preserving scroll
    } catch (err) {
      console.error('Error updating model max output tokens:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update model max output tokens');
      // Reset the input value on error
      setMaxOutputTokensValues(prev => {
        const newValues = { ...prev };
        delete newValues[modelId];
        return newValues;
      });
    }
  };

  const handleRescanModels = async () => {
    setRescanLoading(true);
    try {
      const result = await api.admin.rescanModels();
      
      if (result.success) {
        toast.success(result.message || 'Models rescanned successfully');
        
        // Refresh the models list to show updated data
        await fetchModels();
      } else {
        // Partial success case
        toast.warning(result.message || 'Model rescan completed with some issues');
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach((error: string) => {
            toast.error(error);
          });
        }
        
        // Still refresh models to show any successful updates
        await fetchModels();
      }
    } catch (err) {
      console.error('Error rescanning models:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to rescan models');
    } finally {
      setRescanLoading(false);
    }
  };

  const handleShowModelModal = async () => {
    setShowModelModal(true);
    await Promise.all([fetchModels(), fetchProviders()]);
  };

  // API Key Management Functions
  const fetchAPIKeys = async () => {
    try {
      setAPIKeysLoading(true);
      const response = await api.admin.getAPIKeys();
      setAPIKeys(response.apiKeys || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to fetch API keys');
    } finally {
      setAPIKeysLoading(false);
    }
  };

  const handleShowAPIKeyModal = () => {
    setShowAPIKeyModal(true);
    fetchAPIKeys();
  };

  // Timeout Monitoring Functions
  const fetchTimeoutMetrics = async () => {
    try {
      setTimeoutLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/timeout-metrics', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch timeout metrics: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTimeoutMetrics(data);
    } catch (error) {
      console.error('Error fetching timeout metrics:', error);
      toast.error('Failed to fetch timeout metrics');
    } finally {
      setTimeoutLoading(false);
    }
  };

  const handleShowTimeoutModal = () => {
    setShowTimeoutModal(true);
    fetchTimeoutMetrics();
  };

  // Version Functions
  const fetchVersionInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/version', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch version info: ${response.statusText}`);
      }
      
      const data = await response.json();
      setVersionInfo(data);
    } catch (error) {
      console.error('Error fetching version info:', error);
      // Don't show toast error for version info as it's not critical
    }
  };

  const handleAddAPIKey = async () => {
    if (!newAPIKey.provider || !newAPIKey.name || !newAPIKey.keyValue) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await api.admin.createAPIKey(newAPIKey.provider, newAPIKey.name, newAPIKey.keyValue);
      toast.success('API key added successfully');
      setShowAddKeyModal(false);
      setNewAPIKey({ provider: '', name: '', keyValue: '' });
      fetchAPIKeys();
    } catch (error) {
      console.error('Error adding API key:', error);
      toast.error('Failed to add API key');
    }
  };

  const handleDeleteAPIKey = async (keyId: string) => {
    console.log('handleDeleteAPIKey called with keyId:', keyId);
    
    if (!window.confirm('Are you sure you want to delete this API key?')) {
      console.log('User cancelled delete');
      return;
    }

    try {
      console.log('Calling deleteAPIKey API...');
      await api.admin.deleteAPIKey(keyId);
      console.log('Delete successful');
      toast.success('API key deleted successfully');
      fetchAPIKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete API key');
    }
  };

  const handleToggleAPIKey = async (keyId: string, isActive: boolean) => {
    try {
      await api.admin.toggleAPIKey(keyId, isActive);
      toast.success(`API key ${isActive ? 'activated' : 'deactivated'} successfully`);
      fetchAPIKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
      toast.error('Failed to update API key status');
    }
  };

  const handleAddUserClick = () => {
    setNewUserData({
      name: '',
      email: '',
      password: '',
      role: 'user'
    });
    setShowAddUserModal(true);
  };

  const handleNewUserFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const createdUser = await api.admin.createUser(newUserData);
      
      // Add the new user to the list
      setUsers(prev => [...prev, createdUser]);
      setShowAddUserModal(false);
      toast.success('User created successfully');
    } catch (err) {
      console.error('Error creating user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
            {versionInfo && (
              <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                <span className="inline-flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v1a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4zM3 8h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                  </svg>
                  Version: {versionInfo.version}
                </span>
                <span className="inline-flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {versionInfo.environment}
                </span>
                <span className="inline-flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Uptime: {versionInfo.uptime}
                </span>
              </div>
            )}
          </div>
          <div className="flex space-x-2">
            {onOpenSettings && (
              <button 
                onClick={onOpenSettings}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Global Settings
              </button>
            )}
            <button 
              onClick={handleAddUserClick}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add User
            </button>
            <button 
              onClick={() => navigate('/admin/database')}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 1.79 4 4 4h8c2.21 0 4-1.79 4-4V7M4 7c0-2.21 1.79-4 4-4h8c2.21 0 4 1.79 4-4V7M4 7h16" />
              </svg>
              Database Viewer
            </button>
            <button 
              onClick={() => setShowCacheWipeModal(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Wipe Cache
            </button>
            <button 
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Refresh
            </button>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Manage user accounts and their roles.
        </p>

        {/* System Settings Section */}
        <div className="mt-6 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">System Settings</h3>
            
            <div className="space-y-4">
              {/* Registration Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">User Registration</label>
                  <p className="text-sm text-gray-500">Allow new users to create accounts</p>
                </div>
                <div className="flex items-center">
                  <button
                    type="button"
                    disabled={settingsLoading}
                    onClick={() => handleRegistrationToggle(!registrationEnabled)}
                    className={`${
                      registrationEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <span
                      className={`${
                        registrationEnabled ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                  <span className={`ml-3 text-sm font-medium ${registrationEnabled ? 'text-green-600' : 'text-red-600'}`}>
                    {registrationEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Backup & Restore Section */}
        <div className="mt-6 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Backup & Restore</h3>
            <p className="text-sm text-gray-500 mb-4">
              Export all system data (users, workflows, blocks, agents) for backup purposes or import a previous backup.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Export System Backup</label>
                  <p className="text-sm text-gray-500">Download a complete backup of all system data</p>
                </div>
                <button
                  type="button"
                  disabled={backupLoading}
                  onClick={handleExportBackup}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {backupLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Backup
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Import System Backup</label>
                  <p className="text-sm text-gray-500">Restore data from a previous backup file</p>
                </div>
                <button
                  type="button"
                  disabled={backupLoading}
                  onClick={handleImportClick}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Import Backup
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Important Notes
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Backups do not include user passwords for security reasons</li>
                        <li>Imported users will need to reset their passwords</li>
                        <li>Admin users are not imported to prevent conflicts</li>
                        <li>Existing data with the same IDs will be skipped during import</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model Management Section */}
        <div className="mt-6 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">LLM Model Management</h3>
            <p className="text-sm text-gray-500 mb-4">
              Manage available language models for blocks and agents. Enable or disable models as needed. Use "Rescan Models" to fetch the latest models from provider APIs.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Manage Models</label>
                  <p className="text-sm text-gray-500">View and configure available LLM models</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleRescanModels}
                    disabled={rescanLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {rescanLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Rescanning...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Rescan Models
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleShowModelModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Manage Models
                  </button>
                </div>
              </div>
            </div>

            {/* API Key Management */}
            <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">API Key Management</label>
                  <p className="text-sm text-gray-500">Manage API keys for different LLM providers (OpenAI, Anthropic, Google)</p>
                </div>
                <button
                  type="button"
                  onClick={handleShowAPIKeyModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2m0 0V5a2 2 0 012-2h4a2 2 0 012 2v2M9 7h6" />
                  </svg>
                  Manage API Keys
                </button>
              </div>
            </div>

            {/* Prompt Optimization Settings */}
            <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-700">Prompt Optimization Settings</label>
                <p className="text-sm text-gray-500 mb-4">Configure the AI model and template used for optimizing prompts in block editors</p>
                
                <div className="space-y-4">
                  {/* Model Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Optimization Model</label>
                    <Autocomplete
                      fullWidth
                      options={models.filter(m => !m.deprecated)}
                      value={models.find(m => m.id === promptOptimizerSettings.model) || null}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setPromptOptimizerSettings({...promptOptimizerSettings, model: newValue.id});
                        }
                      }}
                      getOptionLabel={(option) => {
                        const label = `${option.provider?.toUpperCase()} / ${option.name}`;
                        return option.enabled ? label : `${label} (Disabled)`;
                      }}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchLower = inputValue.toLowerCase();
                        return options.filter((option) =>
                          option.name.toLowerCase().includes(searchLower) ||
                          option.provider?.toLowerCase().includes(searchLower) ||
                          option.id.toLowerCase().includes(searchLower)
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={models.length === 0 ? "Loading models..." : "Search models..."}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {option.provider?.toUpperCase()} / {option.name}
                            </span>
                            {!option.enabled && (
                              <span className="text-xs text-gray-500">(Disabled)</span>
                            )}
                          </div>
                        </li>
                      )}
                      loading={models.length === 0}
                      disabled={models.length === 0}
                    />
                    <p className="mt-1 text-xs text-gray-500">Choose a fast, cost-effective model (e.g., gpt-4o-mini)</p>
                  </div>

                  {/* Prompt Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Optimization Prompt Template</label>
                    <textarea
                      rows={4}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={promptOptimizerSettings.prompt}
                      onChange={(e) => setPromptOptimizerSettings({...promptOptimizerSettings, prompt: e.target.value})}
                      placeholder="Instructions for the LLM on how to optimize prompts..."
                    />
                    <p className="mt-1 text-xs text-gray-500">This prompt tells the AI how to enhance user prompts</p>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleSavePromptOptimizerSettings}
                      disabled={promptOptimizerLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      {promptOptimizerLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeout Monitoring */}
            <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Timeout Monitoring</label>
                  <p className="text-sm text-gray-500">Monitor LLM API timeouts and performance issues across all providers</p>
                </div>
                <button
                  type="button"
                  onClick={handleShowTimeoutModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  View Timeouts
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
            <p className="font-bold">Error</p>
            <p>{error}</p>
          </div>
        )}
        
        <div className="mt-4">
          {users.length === 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
              <p className="text-gray-500">No users found.</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {users.map(user => (
                  <li key={user.id}>
                    <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{user.name}</h3>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="mt-1 flex items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {user.role}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            Last login: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            Last accessed: {user.lastAccessedAt ? new Date(user.lastAccessedAt).toLocaleDateString() : 'Never'}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-lg sm:w-full">
            <form onSubmit={handleUpdateUser}>
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Edit User</h3>
                <div className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-lg sm:w-full">
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Delete User</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the user "{deleteConfirmUser.name}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={handleCancelDelete}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-lg sm:w-full">
            <form onSubmit={handleAddUser}>
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Add New User</h3>
                <div className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={newUserData.name}
                      onChange={handleNewUserFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={newUserData.email}
                      onChange={handleNewUserFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      id="password"
                      required
                      value={newUserData.password}
                      onChange={handleNewUserFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={newUserData.role}
                      onChange={handleNewUserFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddUserModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Backup Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-lg sm:w-full">
            <form onSubmit={handleImportBackup}>
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Import System Backup</h3>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Select a backup file to restore system data. This will import users, workflows, blocks, and agents.
                      </p>
                      
                      <div className="mb-4">
                        <label htmlFor="backup-file" className="block text-sm font-medium text-gray-700 mb-2">
                          Backup File (JSON)
                        </label>
                        <input
                          type="file"
                          id="backup-file"
                          accept=".json"
                          onChange={handleFileChange}
                          required
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                        />
                      </div>

                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                              Warning
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>This operation will add data to your system. Make sure you trust the backup file source.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={!importFile || backupLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {backupLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importing...
                    </>
                  ) : (
                    'Import Backup'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Model Management Modal - Spreadsheet Style */}
      {showModelModal && (
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={() => setShowModelModal(false)}
        >
          <div 
            className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-7xl sm:w-full h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowModelModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label="Close modal"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-purple-100">
                  <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">LLM Model Management</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Enable or disable language models for use in blocks and agents.
                  </p>
                </div>
              </div>
              
              {/* Filters Row */}
              <div className="mt-4">
                {/* Search Input */}
                <div className="mb-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search models by name..."
                      value={modelSearchText}
                      onChange={(e) => setModelSearchText(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-10"
                    />
                    <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {modelSearchText && (
                      <button
                        onClick={() => setModelSearchText('')}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Provider Filter Pills */}
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-xs font-medium text-gray-700">Filter:</span>
                      {(() => {
                        // Calculate model counts per provider
                        const providerCounts = models.reduce((acc: any, model) => {
                          const provider = model.provider || 'unknown';
                          acc[provider] = (acc[provider] || 0) + 1;
                          return acc;
                        }, {});
                        
                        return (
                          <>
                            <button
                              onClick={() => setSelectedProvider('all')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                selectedProvider === 'all'
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              All ({models.length})
                            </button>
                            <button
                              onClick={() => setSelectedProvider('openai')}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                selectedProvider === 'openai'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                            >
                              OpenAI ({providerCounts['openai'] || 0})
                            </button>
                            <button
                              onClick={() => setSelectedProvider('anthropic')}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                selectedProvider === 'anthropic'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              }`}
                            >
                              Anthropic ({providerCounts['anthropic'] || 0})
                            </button>
                            <button
                              onClick={() => setSelectedProvider('google')}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                selectedProvider === 'google'
                                  ? 'bg-yellow-600 text-white'
                                  : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              }`}
                            >
                              Google ({providerCounts['google'] || 0})
                            </button>
                            <button
                              onClick={() => setSelectedProvider('openrouter')}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                selectedProvider === 'openrouter'
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                              }`}
                            >
                              OpenRouter ({providerCounts['openrouter'] || 0})
                            </button>
                          </>
                        );
                      })()}
                      
                      <span className="mx-2 text-gray-300">|</span>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={showAllModels}
                          onChange={(e) => {
                            setShowAllModels(e.target.checked);
                            if (models.length > 0) fetchModels();
                          }}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 h-4 w-4"
                        />
                        <span className="ml-1 text-xs text-gray-600">All</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={showDeprecatedModels}
                          onChange={(e) => setShowDeprecatedModels(e.target.checked)}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 h-4 w-4"
                        />
                        <span className="ml-1 text-xs text-gray-600">Show Deprecated</span>
                      </label>
                    </div>
                    <span className="text-xs text-gray-500">
                      {models.filter((model) => {
                        if (selectedProvider !== 'all' && model.provider !== selectedProvider) return false;
                        if (!showDeprecatedModels && model.deprecated) return false;
                        if (modelSearchText && !model.name.toLowerCase().includes(modelSearchText.toLowerCase()) && !model.id.toLowerCase().includes(modelSearchText.toLowerCase())) return false;
                        return true;
                      }).length} / {models.length} models
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Spreadsheet Table */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {modelsLoading ? (
                <div className="flex justify-center items-center flex-1">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <>
                  {(() => {
                    const filteredModels = models.filter((model) => {
                      if (selectedProvider !== 'all' && model.provider !== selectedProvider) return false;
                      if (!showDeprecatedModels && model.deprecated) return false;
                      // Search in both name and ID (exact substring match)
                      if (modelSearchText) {
                        const searchLower = modelSearchText.toLowerCase();
                        const nameMatch = model.name.toLowerCase().includes(searchLower);
                        const idMatch = model.id.toLowerCase().includes(searchLower);
                        if (!nameMatch && !idMatch) return false;
                      }
                      return true;
                    });

                    if (filteredModels.length === 0) {
                      return (
                        <div className="flex-1 flex items-center justify-center">
                          <div className="text-center py-8">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No models found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {selectedProvider !== 'all' 
                                ? `No ${selectedProvider} models match your filters.`
                                : 'No models match your filters.'}
                            </p>
                            <div className="mt-4">
                              <button
                                onClick={() => {
                                  setSelectedProvider('all');
                                  setShowDeprecatedModels(true);
                                }}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                              >
                                Reset Filters
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <>
                        {/* Table Header - Fixed */}
                        <div className="bg-gray-50 border-b border-gray-200">
                          <div className="grid grid-cols-[2fr_1fr_120px_80px_80px_80px_80px_100px] gap-2 px-4 py-2 text-xs font-semibold text-gray-700">
                            <div>Model</div>
                            <div>Status</div>
                            <div className="text-center">Cost/1K</div>
                            <div className="text-center">Timeout</div>
                            <div className="text-center">Max Tokens</div>
                            <div className="text-center">Cache</div>
                            <div className="text-center">Enabled</div>
                            <div className="text-center">Actions</div>
                          </div>
                        </div>

                        {/* Table Body - Scrollable */}
                        <div ref={modelsContainerRef} className="flex-1 overflow-y-auto">
                          {filteredModels.map((model) => (
                            <div 
                              key={model.id} 
                              className={`grid grid-cols-[2fr_1fr_120px_80px_80px_80px_80px_100px] gap-2 px-4 py-2.5 text-xs border-b border-gray-100 hover:bg-gray-50 ${
                                model.deprecated ? 'opacity-60' : ''
                              }`}
                            >
                              {/* Model Name Column */}
                              <div className="flex flex-col min-w-0">
                                <div className="flex items-center space-x-2 min-w-0">
                                  <span className={`${
                                    model.provider === 'openai' ? 'text-green-700' :
                                    model.provider === 'anthropic' ? 'text-blue-700' :
                                    model.provider === 'google' ? 'text-yellow-700' :
                                    model.provider === 'openrouter' ? 'text-purple-700' :
                                    'text-gray-700'
                                  } font-semibold text-[10px] uppercase flex-shrink-0`}>
                                    {model.provider}
                                  </span>
                                  <span className="text-gray-900 font-medium truncate" title={`${model.name}\n${model.id}`}>
                                    {model.name}
                                  </span>
                                </div>
                                <span className="text-gray-500 text-[10px] truncate ml-0 mt-0.5" title={model.id}>
                                  {model.id}
                                </span>
                              </div>

                              {/* Status Badges Column */}
                              <div className="flex items-center space-x-1 flex-wrap">
                                {model.isDefault && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800">
                                    Default
                                  </span>
                                )}
                                {model.deprecated && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800">
                                    Deprecated
                                  </span>
                                )}
                                {model.cacheable === false && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800" title="Non-cacheable">
                                    No-Cache
                                  </span>
                                )}
                              </div>

                              {/* Cost Column */}
                              <div className="flex items-center justify-center text-gray-600">
                                {model.costPer1k && model.costPer1k.input && model.costPer1k.output ? (
                                  <span className="text-[10px]" title={`Input: $${model.costPer1k.input} / Output: $${model.costPer1k.output} per 1K tokens`}>
                                    ${model.costPer1k.input}/${model.costPer1k.output}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-[10px]">-</span>
                                )}
                              </div>

                              {/* Timeout Input */}
                              <div className="flex items-center justify-center">
                                <input
                                  type="number"
                                  min="5"
                                  max="600"
                                  value={timeoutValues[model.id] !== undefined ? timeoutValues[model.id] : (model.timeoutSeconds || 30)}
                                  onChange={(e) => handleTimeoutChange(model.id, parseInt(e.target.value))}
                                  onBlur={(e) => handleTimeoutUpdate(model.id, parseInt(e.target.value))}
                                  disabled={model.deprecated}
                                  className="w-14 px-1 py-1 text-xs text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Timeout in seconds"
                                />
                              </div>

                              {/* Max Tokens Input */}
                              <div className="flex items-center justify-center">
                                <input
                                  type="number"
                                  min="100"
                                  max="128000"
                                  value={maxOutputTokensValues[model.id] !== undefined ? maxOutputTokensValues[model.id] : (model.maxOutputTokens || 16000)}
                                  onChange={(e) => handleMaxOutputTokensChange(model.id, parseInt(e.target.value))}
                                  onBlur={(e) => handleMaxOutputTokensUpdate(model.id, parseInt(e.target.value))}
                                  disabled={model.deprecated}
                                  className="w-16 px-1 py-1 text-xs text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Max output tokens"
                                />
                              </div>

                              {/* Cacheable Checkbox */}
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={model.cacheable}
                                  onChange={() => handleCacheableToggle(model.id, !model.cacheable)}
                                  disabled={model.deprecated}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={`${model.cacheable ? 'Cacheable' : 'Non-cacheable'}`}
                                />
                              </div>

                              {/* Enabled Checkbox */}
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={model.enabled}
                                  onChange={() => handleModelStatusToggle(model.id, !model.enabled)}
                                  disabled={model.deprecated}
                                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={`${model.enabled ? 'Enabled' : 'Disabled'}`}
                                />
                              </div>

                              {/* Actions Column */}
                              <div className="flex items-center justify-center">
                                {!model.isDefault && model.enabled && !model.deprecated && (
                                  <button
                                    type="button"
                                    onClick={() => handleSetDefaultModel(model.id)}
                                    className="px-2 py-1 text-[10px] font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded transition-colors"
                                    title="Set as default model"
                                  >
                                    Set Default
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* API Key Management Modal */}
      {showAPIKeyModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2m0 0V5a2 2 0 012-2h4a2 2 0 012 2v2M9 7h6" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">API Key Management</h3>
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-500">
                        Manage API keys for different LLM providers. Only one key per provider can be active at a time.
                      </p>
                      <button
                        onClick={() => setShowAddKeyModal(true)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add API Key
                      </button>
                    </div>

                    {apiKeysLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {apiKeys.length === 0 ? (
                          <div className="text-center py-8">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2m0 0V5a2 2 0 012-2h4a2 2 0 012 2v2M9 7h6" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys</h3>
                            <p className="mt-1 text-sm text-gray-500">Get started by adding your first API key.</p>
                          </div>
                        ) : (
                          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key Preview</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {apiKeys.map((key) => (
                                  <tr key={key.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          key.provider === 'openai' ? 'bg-green-100 text-green-800' :
                                          key.provider === 'anthropic' ? 'bg-orange-100 text-orange-800' :
                                          key.provider === 'google' ? 'bg-blue-100 text-blue-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {key.provider.charAt(0).toUpperCase() + key.provider.slice(1)}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{key.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{key.keyPreview}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        key.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {key.isActive ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleToggleAPIKey(key.id, !key.isActive);
                                        }}
                                        className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded cursor-pointer ${
                                          key.isActive 
                                            ? 'text-red-700 bg-red-100 hover:bg-red-200' 
                                            : 'text-green-700 bg-green-100 hover:bg-green-200'
                                        }`}
                                        type="button"
                                      >
                                        {key.isActive ? 'Deactivate' : 'Activate'}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleDeleteAPIKey(key.id);
                                        }}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 cursor-pointer"
                                        type="button"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => setShowAPIKeyModal(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add API Key Modal */}
      {showAddKeyModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-lg sm:w-full">
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Add API Key</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Provider</label>
                    <select
                      value={newAPIKey.provider}
                      onChange={(e) => setNewAPIKey({ ...newAPIKey, provider: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="">Select a provider</option>
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="google">Google</option>
                      <option value="openrouter">OpenRouter</option>
                    </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <input
                        type="text"
                        value={newAPIKey.name}
                        onChange={(e) => setNewAPIKey({ ...newAPIKey, name: e.target.value })}
                        placeholder="e.g., Production OpenAI Key"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">API Key</label>
                      <input
                        type="password"
                        value={newAPIKey.keyValue}
                        onChange={(e) => setNewAPIKey({ ...newAPIKey, keyValue: e.target.value })}
                        placeholder="Enter your API key"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleAddAPIKey}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Add API Key
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddKeyModal(false);
                  setNewAPIKey({ provider: '', name: '', keyValue: '' });
                }}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timeout Monitoring Modal */}
      {showTimeoutModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg overflow-hidden shadow-xl transform sm:max-w-6xl sm:w-full max-h-[90vh] overflow-y-auto">
            <div className="px-4 pt-5 pb-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 mr-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">LLM Timeout Monitoring</h3>
                    <p className="text-sm text-gray-500">Monitor API timeouts across all LLM providers</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTimeoutModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {timeoutLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                  <span className="ml-3 text-gray-600">Loading timeout data...</span>
                </div>
              ) : timeoutMetrics ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-8 w-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-red-800">Total Timeouts</p>
                          <p className="text-2xl font-semibold text-red-900">{timeoutMetrics.totalTimeouts || 0}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-8 w-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-blue-800">OpenAI Timeouts</p>
                          <p className="text-2xl font-semibold text-blue-900">{timeoutMetrics.timeoutsByProvider?.openai || 0}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-8 w-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-purple-800">Anthropic Timeouts</p>
                          <p className="text-2xl font-semibold text-purple-900">{timeoutMetrics.timeoutsByProvider?.anthropic || 0}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-8 w-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-800">Google Timeouts</p>
                          <p className="text-2xl font-semibold text-green-900">{timeoutMetrics.timeoutsByProvider?.google || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Last Timeout */}
                  {timeoutMetrics.lastTimeout && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">Most Recent Timeout</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-yellow-700">Provider:</span>
                          <span className="ml-1 text-yellow-900">{timeoutMetrics.lastTimeout.provider}</span>
                        </div>
                        <div>
                          <span className="font-medium text-yellow-700">Model:</span>
                          <span className="ml-1 text-yellow-900">{timeoutMetrics.lastTimeout.model}</span>
                        </div>
                        <div>
                          <span className="font-medium text-yellow-700">Type:</span>
                          <span className="ml-1 text-yellow-900">{timeoutMetrics.lastTimeout.timeoutType}</span>
                        </div>
                        <div>
                          <span className="font-medium text-yellow-700">Duration:</span>
                          <span className="ml-1 text-yellow-900">{timeoutMetrics.lastTimeout.duration}</span>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="font-medium text-yellow-700">Time:</span>
                        <span className="ml-1 text-yellow-900">{new Date(timeoutMetrics.lastTimeout.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  )}

                  {/* Recent Timeouts Table */}
                  {timeoutMetrics.recentTimeouts && timeoutMetrics.recentTimeouts.length > 0 ? (
                    <div className="bg-white border border-gray-200 rounded-lg">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900">Recent Timeout Events</h4>
                        <p className="text-sm text-gray-500">Last {timeoutMetrics.recentTimeouts.length} timeout events</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Configured</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {timeoutMetrics.recentTimeouts.slice().reverse().map((timeout: any, index: number) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {new Date(timeout.timestamp).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    timeout.provider === 'openai' ? 'bg-blue-100 text-blue-800' :
                                    timeout.provider === 'anthropic' ? 'bg-purple-100 text-purple-800' :
                                    timeout.provider === 'google' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {timeout.provider}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-mono">{timeout.model}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    timeout.timeoutType === 'context' ? 'bg-red-100 text-red-800' :
                                    timeout.timeoutType === 'connection' ? 'bg-orange-100 text-orange-800' :
                                    timeout.timeoutType === 'read' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {timeout.timeoutType}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-mono">{timeout.duration}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                                  {timeout.configuredTimeout ? `${timeout.configuredTimeout / 1000000000}s` : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeouts Recorded</h3>
                      <p className="text-gray-500">Great! No LLM API timeouts have been detected recently.</p>
                    </div>
                  )}

                  {/* Refresh Button */}
                  <div className="flex justify-center pt-4">
                    <button
                      onClick={fetchTimeoutMetrics}
                      disabled={timeoutLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh Data
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Timeout Data</h3>
                  <p className="text-gray-500">There was an error loading the timeout metrics.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cache Wipe Confirmation Modal */}
      {showCacheWipeModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Wipe Entire Cache</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-4">
                This will permanently delete <strong>ALL</strong> cached agent responses from the system. This action cannot be undone.
              </p>
              <p className="text-sm text-red-600 font-semibold mb-4">
                ⚠️ This will affect all users and all agents!
              </p>
              <p className="text-sm text-gray-700 mb-2">
                To confirm, please type <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">WIPE CACHE</code> below:
              </p>
              <input
                type="text"
                value={cacheWipeConfirmation}
                onChange={(e) => setCacheWipeConfirmation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono text-sm"
                placeholder="Type WIPE CACHE here"
                autoFocus
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowCacheWipeModal(false);
                  setCacheWipeConfirmation('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleCacheWipe}
                disabled={cacheWipeConfirmation !== 'WIPE CACHE'}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Wipe Cache
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 